import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  PointLabelStyleApiModel,
  PointLabelStyleRelationship,
  WithTemplates,
} from '../../../types/models/point-label-style.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchPointLabelStyles } from '../api/point-label-styles.api';
import { useDeletePointLabelStyle } from '../hooks/api/use-point-label-styles.hook';

export const PointLabelStyles = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deletePointLabelStyle, { loading: deletingPointLabelStyle, error: deletePointLabelStyleError }] =
    useDeletePointLabelStyle();

  const columns: ColDef<PointLabelStyleApiModel & WithTemplates>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'templates',
      headerName: 'Template(s)',
      valueGetter: ({ data }) => data?.templates?.map(({ name }) => name).join(', ') ?? '',
    },
    {
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<PointLabelStyleApiModel & WithTemplates>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deletePointLabelStyle({ pointLabelStyleId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Point Label Style',
                message: `Are you sure you want to delete point label style "${node.data?.name}"?`,
                error: deletePointLabelStyleError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getPointLabelStyles: Parameters<typeof InfniteScrollingGrid<PointLabelStyleApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchPointLabelStyles({
      name: filterQuery?.name,
      sort: sortQuery,
      include: [PointLabelStyleRelationship.TEMPLATES],
      cursor,
      limit,
    });

    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title="Civil 3D Point Label Styles" />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Civil 3D Point Label Styles</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Point Label Style
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid
            loading={deletingPointLabelStyle}
            columnDefs={columns}
            onGetRows={getPointLabelStyles}
          />
        </Box>
      </Box>
    </Box>
  );
};
